import { Directive, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { isLightThemeBackground } from '../utils/theme-color.util';
import { strikeoffPriceOrEmpty } from '../utils/strikeoff-price.util';

@Directive()
export abstract class BasePackageGridCardDirective implements OnInit, OnChanges {
  @Input() package: any;

  activeTab: 'single' | 'subscription' = 'single';
  showAllFeatures = false;
  protected readonly defaultVisibleFeaturesCount = 6;

  constructor(protected messageService: NotificationService) {}

  get isLightThemeHeader(): boolean {
    return isLightThemeBackground(this.package?.theme_color);
  }

  ngOnInit(): void {
    this.setDefaultActiveTab();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['package'] && changes['package'].currentValue) {
      this.setDefaultActiveTab();
      this.showAllFeatures = false;
    }
  }

  /**
   * Set the default active tab ('single' or 'subscription') on the instance unless package type is 'detailing'.
   * @example
   * etDefaultActiveTab()
   * undefined
   * @returns {void} No return value; updates this.activeTab when exactly one tab option is available.
   **/
  setDefaultActiveTab(): void {
    if (this.package?.type === 'detailing') {
      return;
    }
    const hasSingle = this.shouldShowSingleTab();
    const hasSubscription = this.shouldShowSubscriptionTab();

    if (!hasSingle && hasSubscription) {
      this.activeTab = 'subscription';
    } else if (hasSingle && !hasSubscription) {
      this.activeTab = 'single';
    }
  }

  /**
   * Switches the active pricing tab to 'single' or 'subscription' after validating that the selected option is available for the current package.
   * @example
   * witchTab('single')
   * // void — updates this.activeTab or shows an informational message if the chosen option is unavailable
   * @param {'single'|'subscription'} tab - The tab to activate; either 'single' for one-off washes or 'subscription' for monthly/unlimited pricing.
   * @returns {void} Does not return a value; side-effects include updating this.activeTab or displaying an info message when unavailable.
   **/
  switchTab(tab: 'single' | 'subscription'): void {
    if (
      tab === 'single' &&
      (!this.package.single_list_price ||
        this.package.single_list_price === '0' ||
        this.package.single_list_price === '' ||
        parseFloat(this.package.single_list_price) <= 0)
    ) {
      this.messageService.add({
        severity: 'info',
        summary: 'Single wash unavailable!',
        detail: `"${this.package.package_name}" package doesn't offer single washes`,
      });
      return;
    }

    if (
      tab === 'subscription' &&
      (!this.package.monthly_list_price ||
        this.package.monthly_list_price === '0' ||
        this.package.monthly_list_price === '' ||
        parseFloat(this.package.monthly_list_price) <= 0)
    ) {
      this.messageService.add({
        severity: 'info',
        summary: 'Unlimited wash unavailable!',
        detail: `"${this.package.package_name}" package does not offer monthly pricing`,
      });
      return;
    }

    this.activeTab = tab;
  }

  getCurrentPrice(): string {
    return this.activeTab === 'single'
      ? this.package.single_list_price || '0'
      : this.package.monthly_list_price || '0';
  }

  getStrikeoffPrice(): string | null {
    const raw =
      this.activeTab === 'single'
        ? this.package.single_strikeoff_price
        : this.package.monthly_strikeoff_price;
    return strikeoffPriceOrEmpty(raw) || null;
  }

  getPriceLabel(): string {
    return 'LIST PRICE';
  }

  shouldShowSingleTab(): boolean {
    return (
      this.package.type !== 'detailing' &&
      this.package.wash_types &&
      this.package.wash_types.includes('single_wash')
    );
  }

  shouldShowSubscriptionTab(): boolean {
    return (
      this.package.type !== 'detailing' &&
      this.package.wash_types &&
      this.package.wash_types.includes('monthly_membership')
    );
  }

  shouldShowTabs(): boolean {
    return this.shouldShowSingleTab() || this.shouldShowSubscriptionTab();
  }

  get hasFeatures(): boolean {
    return this.getFeatures().length > 0;
  }

  get displayedFeatures(): string[] {
    const allFeatures = this.getFeatures();
    if (this.showAllFeatures) {
      return allFeatures;
    }
    return allFeatures.slice(0, this.defaultVisibleFeaturesCount);
  }

  get hasHiddenFeatures(): boolean {
    return this.getFeatures().length > this.defaultVisibleFeaturesCount;
  }

  toggleFeaturesVisibility(): void {
    this.showAllFeatures = !this.showAllFeatures;
  }

  get featuresToggleLabel(): string {
    return this.showAllFeatures ? 'Show less' : 'Show all';
  }

  /** Add-on packages use variant pricing, not a wash feature bullet list. */
  isAddOnPackage(): boolean {
    if (!this.package) {
      return false;
    }
    const type = String(this.package.type ?? '').toLowerCase();
    if (type === 'add_on' || type === 'addon') {
      return true;
    }
    const wt = this.package.wash_types;
    if (Array.isArray(wt)) {
      return wt.some(
        (w: any) => String(w).toLowerCase() === 'add_on' || String(w).toLowerCase() === 'addon'
      );
    }
    return false;
  }

  private getFeatures(): string[] {
    if (!Array.isArray(this.package?.features)) {
      return [];
    }
    return this.package.features
      .map((feature: any) => {
        if (typeof feature === 'string') {
          return feature.trim();
        }
        if (feature && typeof feature === 'object' && 'name' in feature) {
          const name = (feature as { name?: any }).name;
          return typeof name === 'string' ? name.trim() : '';
        }
        return '';
      })
      .filter((feature: string) => feature.length > 0);
  }
}
